import { useMemo } from 'react';
import type { ConnectionPlayer, ConnectionRequest, ConnectionsSummary } from 'types/connections';
import {
  UserConnectionRequestStatusesEnum,
  useGetConnectionsSummaryQuery,
} from 'types/generated/client';
import { useViewer } from 'hooks/useViewer';
import { mapConnectionPlayer, mapSharedGame } from './mappers';

/** Shared games shown on a request card. The design lists a couple, not a history. */
const SHARED_GAME_LIMIT = 5;

/**
 * The whole connections surface, from one cached query.
 *
 * Every screen that shows connections calls this, so it is deliberately a single
 * document: Apollo dedupes the concurrent calls into one request and they all
 * invalidate together after a write.
 */
export const useConnections = (): ConnectionsSummary => {
  const { userId } = useViewer();

  const { data, loading, refetch } = useGetConnectionsSummaryQuery({
    variables: { userId, sharedGameLimit: SHARED_GAME_LIMIT },
    skip: !userId,
  });

  return useMemo(() => {
    const playedWithRows = data?.userPlayedWith ?? [];
    const dismissedIds = new Set(
      (data?.userPlayedWithDismissals ?? []).map((row) => row.dismissedUserId),
    );
    const blockedUserIds = (data?.userBlocks ?? []).map((row) => row.blockedUserId);
    const blockedIds = new Set(blockedUserIds);
    const sent = data?.sentConnectionRequests ?? [];
    const pendingRequestIdByUserId = new Map(
      sent
        .filter((row) => row.status === UserConnectionRequestStatusesEnum.Pending)
        .map((row) => [row.recipientUserId as string, row.id as string]),
    );
    // Mirrors the server's two-attempt cap; ended connections and lifted blocks
    // archive the history, so only attempts since then are visible here.
    const exhaustedIds = new Set(
      sent
        .filter(
          (row) =>
            row.attemptNumber >= 2 && row.status !== UserConnectionRequestStatusesEnum.Pending,
        )
        .map((row) => row.recipientUserId as string),
    );

    // The played-with view is the only source for games-together, so it feeds the
    // connection rows too rather than being just the second tab's data.
    const playedWithByUserId = new Map(
      playedWithRows.flatMap((row) => (row.otherUserId ? [[row.otherUserId as string, row]] : [])),
    );

    const connections: ConnectionPlayer[] = (data?.userConnections ?? [])
      .flatMap((connection) => {
        // The pair is stored sorted, so which column is "the other player"
        // depends on where the viewer landed.
        const isViewerA = connection.userAId === userId;
        const otherUserId = (isViewerA ? connection.userBId : connection.userAId) as string;
        const profile = isViewerA ? connection.userBProfile : connection.userAProfile;

        if (blockedIds.has(otherUserId)) return [];

        return [
          mapConnectionPlayer({
            profile,
            gamesTogether: playedWithByUserId.get(otherUserId)?.gamesTogether,
            fallbackId: otherUserId,
          }),
        ];
      })
      // Most frequent partners first — what "Top connections" on the You page means.
      .sort((a, b) => b.gamesTogether - a.gamesTogether);

    const connectedIds = new Set(connections.map((player) => player.id));

    const playedWith: ConnectionPlayer[] = playedWithRows.flatMap((row) => {
      const otherUserId = row.otherUserId as string | undefined;
      if (!otherUserId) return [];
      // Played with is "met in a game but not connected", minus anyone the viewer
      // has hidden or blocked.
      if (connectedIds.has(otherUserId) || dismissedIds.has(otherUserId)) return [];
      if (blockedIds.has(otherUserId)) return [];

      return [
        mapConnectionPlayer({
          profile: row.otherUserProfile,
          gamesTogether: row.gamesTogether,
          pendingRequestId: pendingRequestIdByUserId.get(otherUserId) ?? null,
          canRequest: !exhaustedIds.has(otherUserId),
          fallbackId: otherUserId,
        }),
      ];
    });

    const hiddenPlayers: ConnectionPlayer[] = playedWithRows.flatMap((row) => {
      const otherUserId = row.otherUserId as string | undefined;
      if (!otherUserId || !dismissedIds.has(otherUserId) || blockedIds.has(otherUserId)) return [];
      return [
        mapConnectionPlayer({
          profile: row.otherUserProfile,
          gamesTogether: row.gamesTogether,
          fallbackId: otherUserId,
        }),
      ];
    });

    const blockedPlayers: ConnectionPlayer[] = (data?.userBlocks ?? []).map((block) =>
      mapConnectionPlayer({
        profile: block.blockedProfile,
        gamesTogether: playedWithByUserId.get(block.blockedUserId as string)?.gamesTogether,
        fallbackId: block.blockedUserId as string,
      }),
    );

    const declinedCountByRequester = new Map<string, number>();
    (data?.declinedConnectionRequests ?? []).forEach((row) => {
      const requesterUserId = row.requesterUserId as string;
      declinedCountByRequester.set(
        requesterUserId,
        (declinedCountByRequester.get(requesterUserId) ?? 0) + 1,
      );
    });

    const requests: ConnectionRequest[] = (data?.userConnectionRequests ?? []).flatMap(
      (request) => {
        const requesterUserId = request.requesterUserId as string;
        if (blockedIds.has(requesterUserId)) return [];

        const playedWithRow = playedWithByUserId.get(requesterUserId);

        return [
          {
            id: request.id,
            player: mapConnectionPlayer({
              profile: request.requesterProfile,
              gamesTogether: playedWithRow?.gamesTogether,
              fallbackId: requesterUserId,
            }),
            message: request.message ?? null,
            games: (playedWithRow?.games ?? []).flatMap((game) => {
              const mapped = mapSharedGame(game);
              return mapped ? [mapped] : [];
            }),
            declinedCount: declinedCountByRequester.get(requesterUserId) ?? 0,
          },
        ];
      },
    );

    return {
      connections,
      playedWith,
      requests,
      blockedUserIds,
      hiddenPlayers,
      blockedPlayers,
      isLoading: loading,
      refetch,
    };
  }, [data, loading, refetch, userId]);
};
